import type { EpisodeFileFormat } from "@/api";

/** `CodeEditor` 가 아는 하이라이트 모드. Monaco 는 yaml 문법만 포함하고 나머지는 plaintext 다. */
export type PreviewTextLanguage = "json" | "yaml" | "plaintext";

/**
 * 파일 한 건을 본문으로 그릴 수 있는지, 그릴 수 있다면 어떻게 그리는지.
 *
 * 상한은 **텍스트에만** 적용한다. 이미지는 브라우저가 스트리밍으로 그리고 `<img src>` 하나이므로
 * 큰 파일도 탭을 멈추지 않는다. 반면 수십 MB 의 텍스트를 편집기에 넣으면 미리보기 하나가
 * 탭을 멈춘다.
 */
export type FilePreviewMode =
  | { kind: "text"; language: PreviewTextLanguage }
  | { kind: "textTooLarge"; sizeBytes: number }
  | { kind: "image" }
  | { kind: "none" };

// 인라인으로 그릴 수 있는 포맷 → 하이라이트 모드. 여기 없는 포맷은 메타 + 다운로드다.
const TEXT_FORMATS: ReadonlyMap<EpisodeFileFormat, PreviewTextLanguage> = new Map([
  ["json", "json"],
  ["yaml", "yaml"],
  ["csv", "plaintext"],
  ["txt", "plaintext"],
  ["md", "plaintext"],
  ["log", "plaintext"],
  // jsonl 은 줄마다 문서라서 전체 파싱이 성립하지 않는다. `json` 으로 두면 매 렌더에서 실패할
  // 파싱을 시도하고, 하이라이트도 틀린다.
  ["jsonl", "plaintext"],
]);

const IMAGE_FORMATS: ReadonlySet<EpisodeFileFormat> = new Set<EpisodeFileFormat>(["png", "jpg"]);

// 인라인 상한. 넘으면 본문을 아예 받지 않고 다운로드로 안내한다.
const INLINE_MAX_BYTES = 1024 * 1024;

/**
 * 포맷 → 표현 판정. 크기를 모르면(`null`) 상한을 적용할 근거가 없으므로 인라인한다. 목록이
 * 크기를 주지 않은 파일까지 다운로드로 보내면, 읽을 수 있는 파일을 읽지 못한다.
 */
export function resolveFilePreviewMode(
  format: EpisodeFileFormat,
  sizeBytes: number | null,
): FilePreviewMode {
  if (IMAGE_FORMATS.has(format)) {
    return { kind: "image" };
  }
  const language = TEXT_FORMATS.get(format);
  if (language === undefined) {
    return { kind: "none" };
  }
  if (sizeBytes !== null && sizeBytes > INLINE_MAX_BYTES) {
    return { kind: "textTooLarge", sizeBytes };
  }
  return { kind: "text", language };
}

/**
 * json 은 들여쓰기를 맞춰 보여 준다(한 줄로 저장된 파일이 흔하다). 파싱에 실패하면 원문을
 * 그대로 보여 준다. 깨진 파일일수록 원문을 봐야 무엇이 잘못됐는지 알 수 있다.
 */
export function formatTextForDisplay(body: string, language: PreviewTextLanguage): string {
  if (language !== "json") {
    return body;
  }
  try {
    return JSON.stringify(JSON.parse(body), null, 2) as string;
  } catch {
    return body;
  }
}
